/* eslint-disable camelcase */
/* eslint-disable complexity */
import React from 'react';
import PropTypes from 'prop-types';
import {
    CONSTRUCTOR_PODS, RESULTS_COUNT, PRICE_RANGE, BRANDS
} from 'constants/constructorConstants';
import constants from 'constants/content';
import { Fragment } from 'react';
import { wrapComponent } from 'utils/framework';
import BaseClass from 'components/BaseClass';
import { getCmsComponent } from 'constants/cmsComponentsMapping';
import ConstructorCarousel from 'components/ConstructorCarousel';
import RecServiceCarousel from 'components/RecServiceCarousel';
import Empty from 'constants/empty';
import LazyLoad from 'components/LazyLoad';
import cookieUtils from 'utils/Cookies';
import { SKIN_ANALYSIS_TOOL } from 'constants/arSkincare';
import PageRenderReport from 'components/PageRenderReport/PageRenderReport';
import cmsComponentActions from 'actions/CmsComponentActions';
import dynamicComponentActions from 'actions/dynamicComponentActions';

const HP_CAROUSEL_PRODUCT_NAME_LINES = 3;
const HP_CAROUSEL_DISPLAY_LINES_TEST_PODS = [CONSTRUCTOR_PODS.RFY, CONSTRUCTOR_PODS.SELLING_FAST, CONSTRUCTOR_PODS.NEW_ARRIVALS];
const ELIGIBLE_RATINGS_PODS = [CONSTRUCTOR_PODS.RFY, CONSTRUCTOR_PODS.NEW_ARRIVALS, CONSTRUCTOR_PODS.SELLING_FAST];

const minHeights = {
    ProductList: 340,
    BannerList: 340,
    Banner: 340,
    RewardList: 340,
    Recap: 340,
    CustomRendering: 340,
    SoftLinks: 85,
    Divider: 85
};

const {
    COMPONENT_TYPES: {
        BANNER_LIST,
        BANNER,
        COPY,
        DIVIDER,
        PRODUCT_LIST,
        PROMOTION_LIST,
        RECAP,
        REWARD_LIST,
        SECTION_HEADING,
        SOFT_LINKS,
        SMS_OPTIN,
        SECTION,
        CUSTOM_RENDERING,
        UGC_WIDGET,
        LOVES_LIST
    },
    CONTEXTS,
    COMPONENT_SPACING
} = constants;

const isRecsMigrationEnabled = Sephora.configurationSettings.recsMigration?.isEnabled;

class ComponentList extends BaseClass {
    state = {};

    componentDidMount() {
        const { items = Empty.Array, page = null } = this.props;
        cmsComponentActions.updateComsComponentPageData({
            page,
            items
        });

        if (this.props.userId) {
            dynamicComponentActions.processDynamicComponents(items);
        }

        if (cookieUtils.read(cookieUtils.KEYS.ILLINOIS_YES) === 'true') {
            this.setState({ illinoisHydrated: true });
        }
    }

    componentDidUpdate(prevProps) {
        // Re-run dynamic components when user logs in
        if (prevProps.userId !== this.props.userId) {
            const { items = Empty.Array } = this.props;

            dynamicComponentActions.processDynamicComponents(items);
        }
    }

    render() {
        const {
            context,
            enablePageRenderTracking = false,
            items = Empty.Array,
            page = null,
            trackingCount = 2,
            removeFirstItemMargin = null,
            removeLastItemMargin = null,
            trackSoftLink,
            customStyles,
            customCardSize,
            params
        } = this.props;

        const isIllinoisUser = !!this.state.illinoisHydrated;

        let renderedComponentsCounter = 0;
        let lazyLoadEnabled = false;
        let numberOfComponentsToRenderWithoutLazyLoad = 0;

        return (
            <>
                {items.map((item, index) => {
                    const constructorItem = {};
                    const isLastItem = index === items.length - 1;

                    switch (item.type) {
                        case BANNER:
                            item.shouldTriggerImpression = true;

                            if (item?.action?.page?.slug === SKIN_ANALYSIS_TOOL) {
                                if (isIllinoisUser) {
                                    return null;
                                }
                            }

                            if (!isLastItem && items[index + 1].type === SECTION_HEADING) {
                                item.marginBottom = null;
                            }

                            break;
                        case BANNER_LIST:
                            if (Array.isArray(item?.items)) {
                                item.items = item.items.filter(subItem => !(subItem?.action?.page?.slug === SKIN_ANALYSIS_TOOL && isIllinoisUser));
                            }

                            if (!isLastItem && items[index + 1].type === BANNER) {
                                item.marginBottom = COMPONENT_SPACING.SM;
                            }

                            break;
                        case SECTION_HEADING:
                            if (!isLastItem && items[index + 1]) {
                                items[index + 1].marginTop = null;
                            }

                            break;
                        case PRODUCT_LIST:
                            if (item.skipTypeOverride) {
                                break;
                            }

                            /*
                            Introduced in INFL-1952: overrides the Contentful component type based
                            on targetUrl. A new type should have been created, the CustomRendering
                            type used, or the SID leveraged instead.

                            As a workaround to avoid breaking the Loves List on the Basket page,
                            I’m bypassing this logic using the validation above.

                            In Shop Your Store, we have a ProductList with a /shopping-list targetUrl,
                            and we need it to behave according to its declared type, rather than
                            the synthetic type created below.
                            */
                            if (item.action?.targetUrl === '/shopping-list') {
                                item.type = LOVES_LIST;
                            }

                            break;
                        default:
                            break;
                    }

                    if (!isLastItem && items[index + 1].type === DIVIDER) {
                        item.marginBottom = null;
                    }

                    if (removeFirstItemMargin && index === 0) {
                        item.marginTop = null;
                    }

                    if (removeLastItemMargin && isLastItem) {
                        item.marginBottom = null;
                    }

                    const enableRenderTracking = enablePageRenderTracking && index < trackingCount;
                    const { type, features, ...restItemProps } = constructorItem.type ? constructorItem : item;
                    let ComponentToRender = getCmsComponent({
                        type,
                        features,
                        isAnonymous: this.props.isAnonymous,
                        personalization: restItemProps.personalization
                    });

                    if (!ComponentToRender) {
                        return null;
                    }

                    let propsToRender = {};

                    function preparePropsToRender(props, componentToRender) {
                        const commonPropsToRender = {
                            type,
                            context,
                            enablePageRenderTracking: enableRenderTracking,
                            page,
                            customStyles,
                            customCardSize,
                            ...restItemProps
                        };

                        if (componentToRender) {
                            ComponentToRender = componentToRender;
                        }

                        propsToRender = {
                            ...commonPropsToRender,
                            ...props
                        };
                    }

                    if (item.ctorPodId) {
                        const podIdConfigurations = {
                            [CONSTRUCTOR_PODS.RFY_BASKET]: {
                                condition: params?.itemIds?.length > 0,
                                config: {
                                    params,
                                    podId: CONSTRUCTOR_PODS.RFY_BASKET
                                }
                            },
                            [CONSTRUCTOR_PODS.BOPIS_BASKET]: {
                                condition: params?.itemIds?.length > 0,
                                config: {
                                    params,
                                    podId: CONSTRUCTOR_PODS.BOPIS_BASKET
                                }
                            },
                            [CONSTRUCTOR_PODS.UNDER_20_LOYALTY]: {
                                config: {
                                    params: {
                                        ...params,
                                        ...(!isRecsMigrationEnabled && {
                                            filters: {
                                                Price: PRICE_RANGE.UNDER_TWENTY
                                            }
                                        })
                                    },
                                    podId: CONSTRUCTOR_PODS.UNDER_20_LOYALTY
                                }
                            },
                            [CONSTRUCTOR_PODS.SALE_LOYALTY]: {
                                config: {
                                    params: {
                                        ...params,
                                        ...(!isRecsMigrationEnabled && {
                                            filters: {
                                                on_sale: true
                                            }
                                        })
                                    },
                                    podId: CONSTRUCTOR_PODS.SALE_LOYALTY
                                }
                            },
                            [CONSTRUCTOR_PODS.BASKET_QUICK_ADDS]: {
                                condition: params?.itemIds?.length > 0,
                                config: {
                                    params: {
                                        ...params,
                                        numResults: RESULTS_COUNT.BASKET_QUICK_ADDS,
                                        ...(!isRecsMigrationEnabled && {
                                            filters: {
                                                Price: PRICE_RANGE.UNDER_FIFTEEN,
                                                Brand: BRANDS.SEPHORA_COLLECTION
                                            }
                                        })
                                    },
                                    podId: CONSTRUCTOR_PODS.BASKET_QUICK_ADDS
                                }
                            },
                            [CONSTRUCTOR_PODS.AUTO_REPLENISH_SEPHORA_COLLECTION]: {
                                config: {
                                    params: {
                                        ...params,
                                        ...(!isRecsMigrationEnabled && {
                                            filters: {
                                                Brand: BRANDS.SEPHORA_COLLECTION
                                            }
                                        })
                                    },
                                    podId: CONSTRUCTOR_PODS.AUTO_REPLENISH_SEPHORA_COLLECTION
                                }
                            },
                            default: {
                                config: {
                                    params: {},
                                    podId: item.ctorPodId
                                }
                            }
                        };

                        const podIdKey = item.ctorPodId in podIdConfigurations ? item.ctorPodId : 'default';
                        const { condition = true, config } = podIdConfigurations[podIdKey];

                        if (condition) {
                            const CarouselComponent = isRecsMigrationEnabled ? RecServiceCarousel : ConstructorCarousel;

                            // Dynamically add SHOW_ADD_BUTTON to eligible carousel groupings when feature flag is enabled
                            const ELIGIBLE_ATB_PODS = [CONSTRUCTOR_PODS.RFY, CONSTRUCTOR_PODS.NEW_ARRIVALS, CONSTRUCTOR_PODS.SELLING_FAST];
                            let finalConfig = config;

                            if (ELIGIBLE_ATB_PODS.includes(item.ctorPodId) && this.props.isCarouselsATBEnabled) {
                                const { PRODUCT_LIST_GROUPING } = constants;
                                const { SHOW_ADD_BUTTON } = PRODUCT_LIST_GROUPING;
                                const existingGrouping = item.grouping || [];

                                finalConfig = {
                                    ...config,
                                    grouping: [...existingGrouping, SHOW_ADD_BUTTON]
                                };
                            }

                            if (HP_CAROUSEL_DISPLAY_LINES_TEST_PODS.includes(item.ctorPodId) && this.props.showUpdateProductNameDisplayHP) {
                                finalConfig = {
                                    ...finalConfig,
                                    productNameLines: HP_CAROUSEL_PRODUCT_NAME_LINES
                                };
                            }

                            if (ELIGIBLE_RATINGS_PODS.includes(item.ctorPodId) && this.props.isHomepageCarouselRatingsEnabled) {
                                const { SHOW_RATING_WITH_TOTAL_COUNT, SHOW_RATING_VALUE_WITH_TOTAL_COUNT } = constants.PRODUCT_LIST_GROUPING;
                                // finalConfig.grouping (when set) already merges item.grouping with any prior
                                // additions (e.g. SHOW_ADD_BUTTON from the ATB test above), so it takes priority
                                // over item.grouping. Only fall back to item.grouping when nothing set it yet,
                                // so CMS-configured groupings (price, loves button, etc.) are never dropped.
                                const existingGrouping = finalConfig.grouping || item.grouping || [];

                                finalConfig = {
                                    ...finalConfig,
                                    grouping: [...new Set([...existingGrouping, SHOW_RATING_WITH_TOTAL_COUNT, SHOW_RATING_VALUE_WITH_TOTAL_COUNT])]
                                };
                            }

                            preparePropsToRender(finalConfig, CarouselComponent);
                        }
                    } else {
                        preparePropsToRender({
                            ...restItemProps,
                            trackSoftLink
                        });
                    }

                    // Strategy for enabling LazyLoad
                    switch (page) {
                        // On home page we start wrapping components with LazyLoad after rendering 2 components.
                        // Also we skip counting the following components: DIVIDER, ANCHOR.
                        case 'home':
                        case 'buying-guide':
                        case 'offers': {
                            lazyLoadEnabled = true;

                            // 2 components for Home page
                            // 3 components for Offers page - to pre-render the first 3 before the fold
                            numberOfComponentsToRenderWithoutLazyLoad = page === 'home' ? 2 : 3;

                            switch (type) {
                                case BANNER_LIST:
                                case BANNER:
                                case COPY:
                                case PRODUCT_LIST:
                                case PROMOTION_LIST:
                                case RECAP:
                                case REWARD_LIST:
                                case SECTION_HEADING:
                                case SOFT_LINKS:
                                case SMS_OPTIN:
                                case SECTION:
                                case CUSTOM_RENDERING: {
                                    renderedComponentsCounter++;

                                    break;
                                }
                                case UGC_WIDGET:
                                default: {
                                    break;
                                }
                            }

                            break;
                        }
                        default: {
                            break;
                        }
                    }

                    const renderLazyLoad = lazyLoadEnabled && renderedComponentsCounter > numberOfComponentsToRenderWithoutLazyLoad;

                    // Handler-routed promotion lists resolve to a different handler on sign-in
                    // (e.g. Trending → For-You). LazyLoad captures its component once, so include
                    // auth in the key for those items only: when it flips, React remounts the
                    // lazy subtree and the newly-resolved handler is mounted without a page refresh.
                    const isAuthRoutedPromotionList = type === PROMOTION_LIST && Array.isArray(features) && features.length > 0;
                    const itemKey = `${item.sid || item.type}_${index}${isAuthRoutedPromotionList ? `_${this.props.isAnonymous ? 'anon' : 'auth'}` : ''}`;

                    return (
                        <Fragment key={itemKey}>
                            {renderLazyLoad ? (
                                <LazyLoad
                                    id={item.sid}
                                    component={ComponentToRender}
                                    title={item.title}
                                    minHeight={minHeights[type]}
                                    {...propsToRender}
                                />
                            ) : (
                                <ComponentToRender {...propsToRender} />
                            )}

                            {enableRenderTracking && <PageRenderReport />}
                        </Fragment>
                    );
                })}
            </>
        );
    }
}

ComponentList.propTypes = {
    context: PropTypes.oneOf([CONTEXTS.CONTAINER, CONTEXTS.MODAL]).isRequired,
    enablePageRenderTracking: PropTypes.bool,
    trackingCount: PropTypes.number,
    items: PropTypes.array,
    page: PropTypes.string,
    removeFirstItemMargin: PropTypes.bool,
    removeLastItemMargin: PropTypes.bool
};

export default wrapComponent(ComponentList, 'ComponentList', true);
